import {
  isAllowedByRoles,
  type RoleGateUser,
} from '@/components/Guard/roleGate';

// The Suggest CA card's wire (odin-agents POST /ca-lead/suggest and /ca-lead/assign) and the
// plain-words helpers the card uses. Leave type, reason and dates are never on the wire.

export type CaSuggestStatus = 'suggested' | 'stopped' | 'failed';

export type CaReason = { text: string; cites: string[] };

export type CaPerson = {
  /** A bigint at runtime, so it can arrive as a string: Number() it before comparing or sending. */
  userId: number;
  name: string | null;
  level: number | null;
  openDeals: number | null;
  reasons: CaReason[];
};

export type CaNotEligible = CaPerson & { excluded: string };

export type ComplexitySource =
  | 'line'
  | 'note_words'
  | 'model_quote'
  | 'assumed';

const COMPLEXITY_SOURCES: readonly string[] = [
  'line',
  'note_words',
  'model_quote',
  'assumed',
];

/** An unknown or missing value is null, so an older service still works. */
export const parseComplexitySource = (v: unknown): ComplexitySource | null =>
  typeof v === 'string' && COMPLEXITY_SOURCES.includes(v)
    ? (v as ComplexitySource)
    : null;

/** The line under the quote saying how the complexity was read; none for 'line' or unknown. */
export const complexitySourceNote = (
  source: ComplexitySource | null | undefined,
): string | null =>
  source === 'assumed'
    ? "No complexity note on this deal, so it's treated as normal."
    : source === 'note_words'
      ? "Read as complex from the note's wording."
      : source === 'model_quote'
        ? 'Raised to complex by KALI from this note.'
        : null;

export type CaSuggestionWire = {
  runId: string;
  status: CaSuggestStatus;
  stopCode: string | null;
  /** False: code didn't check leave (off until the leave source is decided, OMT-1761), so the
   * card must not read as "not on leave". A missing or malformed value is read as false. */
  leaveChecked: boolean;
  complexity: 'complex' | 'normal' | null;
  complexityQuote: string | null;
  /** Where the complexity came from. Null from an older service. */
  complexitySource: ComplexitySource | null;
  complexityUpgraded: boolean;
  complexityReason: CaReason | null;
  top: CaPerson | null;
  runnerUp: CaPerson | null;
  eligible: CaPerson[];
  notEligible: CaNotEligible[];
};

export type ReasonCode =
  | 'continuity'
  | 'workload_known'
  | 'complexity_misread'
  | 'availability'
  | 'other';

export type CaAssignBody = {
  runId: string;
  creditAnalystId: number;
  reasonCode?: ReasonCode;
};

export type CaAssignResult = {
  outcome: string;
  hubspotSync: string | null;
  /** On already_decided: the first outcome. */
  decided: string | null;
};

/** The CA lead's tools: admin, the CA lead, and superusers (isAllowedByRoles short-circuits). */
export const canSuggestCa = (user: RoleGateUser | null | undefined): boolean =>
  isAllowedByRoles(user, ['admin', 'mortgage_ca_lead']);

export const REASON_CODES: ReadonlyArray<{ code: ReasonCode; label: string }> =
  [
    { code: 'continuity', label: 'Continuity with the client or file' },
    { code: 'workload_known', label: 'Workload I know about' },
    { code: 'complexity_misread', label: 'Complexity was misread' },
    { code: 'availability', label: 'Availability' },
    { code: 'other', label: 'Other' },
  ];

const hasOwn = (o: object, k: string): boolean =>
  Object.prototype.hasOwnProperty.call(o, k);

const STOP_MESSAGES: Record<string, string> = {
  no_deal:
    'This assessment has no HubSpot deal yet, so there is nobody to assign.',
  deal_not_found:
    "This assessment's HubSpot deal no longer exists in HubSpot. Re-link the deal, then press Suggest again.",
  already_assigned:
    'A Credit Analyst is already assigned to this deal. Nothing to suggest.',
  complexity_not_marked:
    "The EMS note has no 'Complexity:' line yet. Ask the EMS to add it.",
  complexity_unreadable:
    "The 'Complexity:' line in the EMS note isn't clear. Ask the EMS to say complex or normal.",
  nobody_eligible: 'No Credit Analyst is eligible for this deal right now.',
  tools_unavailable: "BrokerCopilot didn't answer. Try again shortly.",
};

/** Why a stopped (or failed) run has no suggestion, in plain words. */
export const stopMessage = (code: string | null | undefined): string =>
  (code && hasOwn(STOP_MESSAGES, code) ? STOP_MESSAGES[code] : undefined) ??
  `Couldn't suggest a Credit Analyst${code ? ` (${code})` : ''}. Try again.`;

const OUTCOME_MESSAGES: Record<string, string> = {
  assigned: 'Assigned. HubSpot is updated.',
  assigned_hubspot_not_synced:
    "Assigned in BrokerCopilot, but HubSpot wasn't updated. Set the Credit Analyst in HubSpot by hand.",
  already_assigned: 'A Credit Analyst is already assigned to this deal.',
  not_eligible:
    "That Credit Analyst isn't eligible for this deal any more. Press Suggest again.",
  reason_required:
    'Choose a reason for picking someone other than the top pick.',
  history_unavailable:
    "Can't record the decision right now, so nothing was assigned. Try again shortly.",
  tools_unavailable:
    "BrokerCopilot didn't answer. Check the Credit Analyst on this deal, then press Suggest again.",
  internal_error:
    "BrokerCopilot didn't answer. Check the Credit Analyst on this deal, then press Suggest again.",
  unknown_run: 'This suggestion has expired. Press Suggest again.',
  not_allowed: 'Only the CA lead and admins can assign a Credit Analyst.',
};

/** What an Assign press came to, in plain words. `decided` is the first outcome on already_decided. */
export const outcomeMessage = (
  outcome: string,
  decided?: string | null,
): string => {
  if (outcome === 'already_decided')
    return decided
      ? `This was already decided (${decided.replace(/_/g, ' ')}). Nothing changed.`
      : 'This was already decided. Nothing changed.';
  if (outcome.startsWith('refused_'))
    return `BrokerCopilot refused the assignment (${outcome.slice('refused_'.length)}). Nothing was assigned.`;
  return (
    (hasOwn(OUTCOME_MESSAGES, outcome)
      ? OUTCOME_MESSAGES[outcome]
      : undefined) ??
    "That didn't go through. Check the deal before trying again."
  );
};

const EXCLUDED_LABELS: Record<string, string> = {
  no_level: 'No CA level set',
  no_owner: 'No HubSpot owner linked',
  leave_unknown: "Leave status couldn't be checked",
  on_leave: 'On leave',
  open_deals_unknown: "Open deals couldn't be counted",
  level_too_low: 'CA level too low for a complex deal',
};

/** Why a Credit Analyst is not eligible, in plain words. */
export const excludedLabel = (code: string): string =>
  (hasOwn(EXCLUDED_LABELS, code) ? EXCLUDED_LABELS[code] : undefined) ??
  `Not eligible (${code})`;

/** A Change (anyone but the top pick) needs a reason; ids can be strings at runtime. */
export const needsReason = (
  chosenId: number,
  topId: number | null | undefined,
): boolean => topId == null || Number(chosenId) !== Number(topId);

/** Outcomes after which the assignment is settled and Assign is disabled. */
export const isDecidedOutcome = (outcome: string): boolean =>
  outcome === 'assigned' ||
  outcome === 'assigned_hubspot_not_synced' ||
  outcome === 'already_decided';

/** The card's caveat when leave wasn't checked: eligibility then says nothing about leave. */
export const leaveNote = (leaveChecked: boolean): string | null =>
  leaveChecked
    ? null
    : "Leave wasn't checked. Confirm the Credit Analyst is in today before you assign.";
